import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import EdilmindLogo from "@/components/EdilmindLogo";
import { formatApiError } from "@/lib/api";
import { Eye, EyeOff, Loader2, ArrowRight, Mail, KeyRound } from "lucide-react";

const TABS = { LOGIN: "login", REGISTER: "register" };

const AuthPage = () => {
  const [params] = useSearchParams();
  const initial = params.get("mode") === "register" ? TABS.REGISTER : TABS.LOGIN;
  const [tab, setTab] = useState(initial);
  const [forgotOpen, setForgotOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]" data-testid="auth-page">
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {/* Brand block */}
          <div className="flex flex-col items-center mb-8">
            <EdilmindLogo size={56} />
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-brand-navy" style={{ fontFamily: "var(--font-display, inherit)" }}>
              EDILMIND
            </h1>
            <p className="mt-1 text-[10px] tracking-[0.32em] uppercase text-brand-navy/60 font-semibold">
              Intelligenza · Ordine · Valore
            </p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-slate-200" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === TABS.LOGIN}
                data-testid="tab-login"
                onClick={() => setTab(TABS.LOGIN)}
                className={`flex-1 py-3 text-sm font-semibold uppercase tracking-wider transition-colors ${
                  tab === TABS.LOGIN
                    ? "text-brand-navy border-b-2 border-brand-orange bg-slate-50"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                Accedi
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === TABS.REGISTER}
                data-testid="tab-register"
                onClick={() => setTab(TABS.REGISTER)}
                className={`flex-1 py-3 text-sm font-semibold uppercase tracking-wider transition-colors ${
                  tab === TABS.REGISTER
                    ? "text-brand-navy border-b-2 border-brand-orange bg-slate-50"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                Registrati
              </button>
            </div>

            <div className="p-6 sm:p-8">
              {forgotOpen ? (
                <ForgotPasswordForm onClose={() => setForgotOpen(false)} />
              ) : tab === TABS.LOGIN ? (
                <LoginForm onForgot={() => setForgotOpen(true)} onSwitch={() => setTab(TABS.REGISTER)} />
              ) : (
                <RegisterForm onSwitch={() => setTab(TABS.LOGIN)} />
              )}
            </div>
          </div>

          {/* Secondary: PIN access link for team members */}
          <div className="mt-6 text-center">
            <Link
              to="/pin-login"
              data-testid="pin-login-link"
              className="text-xs text-slate-500 hover:text-brand-orange transition-colors inline-flex items-center gap-1"
            >
              <KeyRound size={12} /> Sei un membro del team con PIN? Accedi qui
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// LoginForm
// ---------------------------------------------------------------------------
const LoginForm = ({ onForgot, onSwitch }) => {
  const navigate = useNavigate();
  const { loginAccount } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!email.trim() || !password) {
      setErr("Inserisci email e password.");
      return;
    }
    setBusy(true);
    try {
      await loginAccount(email.trim(), password);
      navigate("/");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <Field label="Email" htmlFor="login-email" icon={<Mail size={14} />}>
        <input
          id="login-email" type="email" autoComplete="email" inputMode="email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          disabled={busy} required
          data-testid="login-email-input"
          className="w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none text-sm"
          placeholder="mario.rossi@azienda.it"
        />
      </Field>
      <Field label="Password" htmlFor="login-password" icon={<KeyRound size={14} />}>
        <div className="relative">
          <input
            id="login-password" type={show ? "text" : "password"} autoComplete="current-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            disabled={busy} required
            data-testid="login-password-input"
            className="w-full px-3 py-2.5 pr-10 rounded-md border border-slate-300 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none text-sm"
          />
          <button type="button" onClick={() => setShow((v) => !v)} tabIndex={-1}
            data-testid="login-password-toggle"
            className="absolute inset-y-0 right-0 px-3 text-slate-400 hover:text-brand-navy" aria-label={show ? "Nascondi password" : "Mostra password"}>
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </Field>

      {err && (
        <div data-testid="login-error" className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
          {err}
        </div>
      )}

      <button
        type="submit" disabled={busy}
        data-testid="login-submit-btn"
        className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy text-white font-semibold py-2.5 px-4 rounded-md hover:bg-brand-navy/90 disabled:opacity-50 transition-colors"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
        Accedi
      </button>

      <div className="flex items-center justify-between text-xs pt-2">
        <button type="button" onClick={onForgot} data-testid="forgot-password-btn"
          className="text-slate-500 hover:text-brand-orange transition-colors">
          Password dimenticata?
        </button>
        <button type="button" onClick={onSwitch}
          className="text-slate-500 hover:text-brand-orange transition-colors">
          Crea account →
        </button>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// RegisterForm
// ---------------------------------------------------------------------------
const RegisterForm = ({ onSwitch }) => {
  const navigate = useNavigate();
  const { registerAccount } = useAuth();
  const [form, setForm] = useState({
    nome: "", cognome: "", email: "", password: "", azienda: "",
  });
  const [errors, setErrors] = useState({});
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverErr, setServerErr] = useState(null);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((es) => ({ ...es, [k]: null }));
    setServerErr(null);
  };

  const validate = () => {
    const e = {};
    if (!form.nome.trim()) e.nome = "Nome obbligatorio";
    if (!form.cognome.trim()) e.cognome = "Cognome obbligatorio";
    if (!/.+@.+\..+/.test(form.email.trim())) e.email = "Email non valida";
    if (form.password.length < 8) e.password = "Almeno 8 caratteri";
    if (!form.azienda.trim()) e.azienda = "Azienda obbligatoria";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerErr(null);
    if (!validate()) return;
    setBusy(true);
    try {
      await registerAccount({
        nome: form.nome.trim(),
        cognome: form.cognome.trim(),
        email: form.email.trim(),
        password: form.password,
        azienda: form.azienda.trim(),
      });
      navigate("/");
    } catch (e) {
      setServerErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="register-form">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nome" htmlFor="reg-nome" error={errors.nome}>
          <input
            id="reg-nome" type="text" autoComplete="given-name"
            value={form.nome} onChange={set("nome")} disabled={busy} required
            data-testid="register-nome-input"
            className={inputCls(errors.nome)}
          />
        </Field>
        <Field label="Cognome" htmlFor="reg-cognome" error={errors.cognome}>
          <input
            id="reg-cognome" type="text" autoComplete="family-name"
            value={form.cognome} onChange={set("cognome")} disabled={busy} required
            data-testid="register-cognome-input"
            className={inputCls(errors.cognome)}
          />
        </Field>
      </div>
      <Field label="Email" htmlFor="reg-email" error={errors.email} icon={<Mail size={14} />}>
        <input
          id="reg-email" type="email" autoComplete="email" inputMode="email"
          value={form.email} onChange={set("email")} disabled={busy} required
          data-testid="register-email-input"
          className={inputCls(errors.email)}
          placeholder="mario.rossi@azienda.it"
        />
      </Field>
      <Field label="Azienda" htmlFor="reg-azienda" error={errors.azienda}>
        <input
          id="reg-azienda" type="text" autoComplete="organization"
          value={form.azienda} onChange={set("azienda")} disabled={busy} required
          data-testid="register-azienda-input"
          className={inputCls(errors.azienda)}
          placeholder="Nome della tua azienda"
        />
      </Field>
      <Field label="Password" htmlFor="reg-password" error={errors.password} icon={<KeyRound size={14} />}>
        <div className="relative">
          <input
            id="reg-password" type={show ? "text" : "password"} autoComplete="new-password"
            value={form.password} onChange={set("password")} disabled={busy} required
            data-testid="register-password-input"
            className={`${inputCls(errors.password)} pr-10`}
          />
          <button type="button" onClick={() => setShow((v) => !v)} tabIndex={-1}
            className="absolute inset-y-0 right-0 px-3 text-slate-400 hover:text-brand-navy">
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <p className="mt-1 text-[10px] text-slate-500">Almeno 8 caratteri. Usa una password unica per questo account.</p>
      </Field>

      {serverErr && (
        <div data-testid="register-error" className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
          {serverErr}
        </div>
      )}

      <button
        type="submit" disabled={busy}
        data-testid="register-submit-btn"
        className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange text-white font-semibold py-2.5 px-4 rounded-md hover:bg-brand-orange/90 disabled:opacity-50 transition-colors"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
        Crea account
      </button>

      <p className="text-[11px] text-slate-500 text-center pt-1">
        Hai già un account? <button type="button" onClick={onSwitch} className="text-brand-orange hover:underline">Accedi</button>
      </p>
    </form>
  );
};

// ---------------------------------------------------------------------------
// ForgotPasswordForm
// ---------------------------------------------------------------------------
const ForgotPasswordForm = ({ onClose }) => {
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!email.trim()) {
      setErr("Inserisci la tua email");
      return;
    }
    setBusy(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="forgot-success" className="text-center space-y-4">
        <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
          <Mail size={22} />
        </div>
        <h3 className="text-lg font-bold text-brand-navy">Controlla la tua email</h3>
        <p className="text-sm text-slate-600">
          Se <b>{email}</b> è registrata, riceverai entro pochi minuti un link per reimpostare la password.
          Il link è valido per 30 minuti.
        </p>
        <button onClick={onClose} className="text-xs text-brand-orange hover:underline" data-testid="forgot-back-btn">
          ← Torna al login
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
      <h3 className="text-base font-bold text-brand-navy">Reimposta la password</h3>
      <p className="text-xs text-slate-600">
        Inserisci l&apos;email dell&apos;account. Ti invieremo un link per scegliere una nuova password.
      </p>
      <Field label="Email" htmlFor="forgot-email" icon={<Mail size={14} />}>
        <input
          id="forgot-email" type="email" autoComplete="email" inputMode="email"
          value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} required
          data-testid="forgot-email-input"
          className="w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none text-sm"
        />
      </Field>
      {err && (
        <div data-testid="forgot-error" className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
          {err}
        </div>
      )}
      <div className="flex gap-2">
        <button type="button" onClick={onClose}
          className="flex-1 py-2.5 px-4 rounded-md border border-slate-300 text-slate-600 text-sm hover:bg-slate-50">
          Annulla
        </button>
        <button type="submit" disabled={busy}
          data-testid="forgot-submit-btn"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-navy text-white font-semibold py-2.5 px-4 rounded-md hover:bg-brand-navy/90 disabled:opacity-50">
          {busy && <Loader2 size={14} className="animate-spin" />} Invia link
        </button>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const Field = ({ label, htmlFor, children, icon, error }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
      <span className="inline-flex items-center gap-1">{icon}{label}</span>
    </label>
    {children}
    {error && <p className="mt-1 text-[11px] text-red-600">{error}</p>}
  </div>
);

const inputCls = (err) =>
  `w-full px-3 py-2.5 rounded-md border outline-none text-sm transition-colors ${
    err
      ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-200"
      : "border-slate-300 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
  }`;

export default AuthPage;
